import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import searchService, { SearchItem } from '../services/searchService';
import { formatFileSize } from '../utils/fileUtils';

const SearchResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get('q') || '').trim();

  usePageTitle(query ? `Search: ${query}` : 'Search');

  const [inputValue, setInputValue] = useState(query);
  const [items, setItems] = useState<SearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = useCallback(async (term: string) => {
    if (!term) {
      setItems([]);
      setTotal(0);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await searchService.search({ term, limit: 100 });
      setItems(result.items);
      setTotal(result.pagination.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Search failed');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setInputValue(query);
    void runSearch(query);
  }, [query, runSearch]);

  const submitSearch = () => {
    const next = inputValue.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  };

  const openItem = (item: SearchItem) => {
    if (item.type === 'folder') {
      navigate(`/files/${item.id}`);
      return;
    }
    navigate(`/preview/${item.id}`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <PageHeader
        title="Search"
        description={query ? `Results for “${query}”` : 'Search files and folders across your drive'}
      />

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <TextField
          fullWidth
          size="small"
          placeholder="Search files and folders…"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear"
                  onClick={() => {
                    setInputValue('');
                    setSearchParams({});
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
        />
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && !query && (
        <Alert severity="info">Enter a search term to find files and folders.</Alert>
      )}

      {!loading && !error && query && items.length === 0 && (
        <Alert severity="info">No results found for “{query}”.</Alert>
      )}

      {!loading && items.length > 0 && (
        <Paper variant="outlined" elevation={0}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {total} result{total === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Divider />
          <List disablePadding>
            {items.map((item, index) => (
              <React.Fragment key={`${item.type}-${item.id}`}>
                {index > 0 && <Divider component="li" />}
                <ListItemButton onClick={() => openItem(item)} sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.type === 'folder' ? (
                      <FolderIcon color="primary" />
                    ) : (
                      <InsertDriveFileIcon color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={item.type === 'folder' ? 'Folder' : 'File'}
                          variant="outlined"
                        />
                        {item.type === 'file' && item.size != null && (
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(Number(item.size))}
                          </Typography>
                        )}
                        {item.contentMatch && (
                          <Chip size="small" color="info" label="Content match" />
                        )}
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchResults;
